import type { ViewDetailsResponse } from '@data-access/connect/tc-tcps';
import {
  useApiServices,
  useCapabilityRegistryContext,
  useDataContext,
  usePaneContext,
} from '@mobile-mapping/providers';
import { useConnectProject } from '@trimble/connect-project-providers';
import { useLogger } from '@trimble/solidjs-utils';
import { asError } from '@trimble/logging';
import { t } from '@trimble/i18n';
import { NotificationEventBus } from '@trimble/solidjs-notifications';
import {
  MeasurementsCapabilityProvider,
  ViewsCapabilityProvider,
  ViewWithClipping,
} from '@trimble/web3d-providers';
import { ClippingCapability } from '@trimble/web3d-capabilities';
import { PaneSyncer } from '@trimble/pane-sync';
import type { MultipaneReservedData } from './multipane-view.types';
import { applyMultipaneLayout, applyPaneState } from './apply-multipane-layout';
import {
  applyNavigationStates,
  applyPerPaneViewData,
  applyTrajectories,
} from './apply-multipane-view-data';
import { parseMultipaneReservedData } from './parse-multipane-reserved-data';
import { fromReservedMeasurements } from './reserved-measurements';
import { restoreMeasurementLabels } from './measurement-labels';

/**
 * Returns a callback that loads a saved view: layout → pane state → shared
 * data → trajectories → navigation states → per-pane data.
 */
export function useLoadView(): (view: ViewDetailsResponse) => void {
  const logger = useLogger().getLogger('loadMultipaneView');
  const { tcTcpsApi } = useApiServices();
  const { capabilityRegistry } = useCapabilityRegistryContext();
  const { dataState, dataLoadingService } = useDataContext();
  const { layouts, setLayouts } = usePaneContext();
  const { selectedRegion } = useConnectProject();

  return (view: ViewDetailsResponse) => {
    const region = selectedRegion();
    const viewsProvider = capabilityRegistry.get(ViewsCapabilityProvider)();
    if (!region || !viewsProvider) return;

    void (async () => {
      let reserved: MultipaneReservedData | null = null;
      try {
        const dto = await tcTcpsApi.getReservedViewData(
          region,
          view.id,
          'numark'
        );
        const parsed = parseMultipaneReservedData(dto.data);
        reserved = parsed.data;
        if (parsed.outdated) {
          NotificationEventBus.emit({
            type: 'warning',
            message: t('mobileMapping.views.outdatedReservedData'),
          });
        }
      } catch (error: unknown) {
        logger.debug(
          'Reserved view data unavailable, using single pane',
          undefined,
          asError(error)
        );
      }

      try {
        if (reserved) PaneSyncer.setIsLoadingView(true);
        applyMultipaneLayout(layouts, setLayouts, reserved);
        await applyPaneState(capabilityRegistry, reserved);
        await viewsProvider.loadView(view, region, {
          skipCamera: !!reserved,
        });

        // Alignment (and other non-Connect-native) measurements live in
        // reserved data, so restore them after loadView re-seeds the native
        // markups. A failure here must not abort the rest of the view load.
        if (reserved?.measurements?.length) {
          try {
            const measurementsProvider = capabilityRegistry.get(
              MeasurementsCapabilityProvider
            )();
            measurementsProvider?.restoreMeasurements(
              fromReservedMeasurements(reserved.measurements)
            );
          } catch (error: unknown) {
            logger.error(
              `Failed to restore measurements for view ${view.id}`,
              undefined,
              asError(error)
            );
            NotificationEventBus.emit({
              type: 'warning',
              message: t('mobileMapping.views.measurementsRestoreFailed'),
            });
          }
        }

        // Runs even without reserved data so legacy/Connect-only views still get
        // names; a failure must not abort the rest of the load.
        try {
          const measurementsProvider = capabilityRegistry.get(
            MeasurementsCapabilityProvider
          )();
          if (measurementsProvider) {
            restoreMeasurementLabels(
              measurementsProvider,
              reserved?.connectMeasurementData ?? []
            );
          }
        } catch (error: unknown) {
          logger.error(
            `Failed to restore measurement labels for view ${view.id}`,
            undefined,
            asError(error)
          );
          NotificationEventBus.emit({
            type: 'warning',
            message: t('mobileMapping.views.measurementNamesRestoreFailed'),
          });
        }

        // Reserved data applies clippings pane by pane; otherwise restore here.
        if (!reserved) {
          const viewWithClipping = view as ViewWithClipping;
          for (const pane of capabilityRegistry.paneContainers()) {
            const clippingCap = pane.get(ClippingCapability)();
            if (clippingCap) {
              clippingCap.loadClippingFromConnectData(
                view.sectionPlanes,
                viewWithClipping.sectionBox
              );
            }
          }
        }

        await applyTrajectories(dataLoadingService, dataState, reserved);
        await applyNavigationStates(capabilityRegistry, reserved);
        await new Promise<void>((resolve) => {
          requestAnimationFrame(() => {
            resolve(applyPerPaneViewData(capabilityRegistry, reserved));
          });
        });
      } finally {
        PaneSyncer.setIsLoadingView(false);
      }
    })().catch((error: unknown) =>
      logger.error(`Failed to load view ${view.id}`, undefined, asError(error))
    );
  };
}
